import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Wifi, WifiOff, Monitor, Smartphone, Tablet, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const REASON_LABELS = {
  manual:            { label: 'Manual Logout',          color: 'bg-gray-100 text-gray-700' },
  session_expired:   { label: 'Session Expired',        color: 'bg-amber-100 text-amber-700' },
  session_replaced:  { label: 'Logged in from another device', color: 'bg-red-100 text-red-700' },
  deployment:        { label: 'App Update',             color: 'bg-blue-100 text-blue-700' },
  password_changed:  { label: 'Password Changed',       color: 'bg-purple-100 text-purple-700' },
  force_logout:      { label: 'Force Logout by Admin',  color: 'bg-red-100 text-red-700' },
};

const DeviceIcon = ({ device }) => {
  if (device === 'Mobile') return <Smartphone className="w-3.5 h-3.5" />;
  if (device === 'Tablet') return <Tablet className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
};

const fmt = (iso) => {
  if (!iso) return '—';
  // Append 'Z' if no timezone info — ensures UTC parsing on all browsers incl. iOS Safari
  const utcStr = iso.endsWith('Z') || iso.match(/[+-]\d{2}:\d{2}$/) ? iso : iso + 'Z';
  return new Date(utcStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const utcStr = iso.endsWith('Z') || iso.match(/[+-]\d{2}:\d{2}$/) ? iso : iso + 'Z';
  const diff = Date.now() - new Date(utcStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const UserActivity = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const wsRef = useRef(null);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await api.get('/admin/user-activity');
      setUsers(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + poll every 30s for live updates
  useEffect(() => {
    fetchActivity();
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, [fetchActivity]);

  // WebSocket: refresh instantly when badge_update fires (someone logs in/out)
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '')
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    let ws, ping, reconnect, destroyed = false;
    const connect = () => {
      if (destroyed) return;
      try {
        ws = new WebSocket(`${base}/api/ws/notifications?token=${token}`);
        ws.onopen = () => { ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 25000); };
        ws.onmessage = (e) => {
          if (e.data === 'pong') return;
          try {
            const m = JSON.parse(e.data);
            if (m.type === 'badge_update' || m.type === 'user_activity') fetchActivity();
          } catch {}
        };
        ws.onclose = () => { clearInterval(ping); if (!destroyed) reconnect = setTimeout(connect, 4000); };
        ws.onerror  = () => ws?.close();
      } catch {}
    };
    connect();
    return () => { destroyed = true; clearInterval(ping); clearTimeout(reconnect); ws?.close(); };
  }, [fetchActivity]);

  const onlineCount = users.filter(u => u.is_online).length;

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="user-activity-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">User Activity</h1>
              <p className="text-xs text-emerald-200">
                {onlineCount} online · {users.length} total
                {lastRefresh && <span className="ml-2 opacity-70">· {timeAgo(lastRefresh.toISOString())}</span>}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchActivity} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="bg-white border-b px-4 py-2 flex gap-4 text-xs text-gray-500 flex-shrink-0">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          <span className="font-semibold text-emerald-700">{onlineCount} Online now</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-gray-300 inline-block"></span>
          <span>{users.length - onlineCount} Offline</span>
        </span>
        <span className="ml-auto text-gray-400">Live · auto-refreshes every 30s</span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-emerald-500" /></div>
        ) : users.map(u => (
          <div key={u.user_id} className={`bg-white rounded-xl border overflow-hidden shadow-sm ${u.is_online ? 'border-emerald-200' : 'border-gray-200'}`} data-testid={`user-row-${u.username}`}>
            {/* Main row */}
            <div
              className="px-4 py-3 flex items-center gap-3 cursor-pointer"
              onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
            >
              {/* Avatar */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${u.is_online ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {u.username.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900">{u.username}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 capitalize">{u.role}</span>
                  {!u.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">Deactivated</span>}
                </div>
                <p className="text-xs text-gray-400 truncate">{u.email}</p>
                {/* Status line */}
                <div className="flex items-center gap-1.5 mt-0.5">
                  {u.is_online ? (
                    <>
                      <Wifi className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-emerald-600 font-medium">
                        Online{u.active_connections > 1 ? ` · ${u.active_connections} devices` : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-400">
                        {u.last_logout
                          ? `Logged out ${timeAgo(u.last_logout.timestamp)}`
                          : u.last_login
                          ? `Last seen ${timeAgo(u.last_login.timestamp)}`
                          : 'Never logged in'}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Last logout reason badge */}
              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                {u.last_logout?.reason && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${(REASON_LABELS[u.last_logout.reason] || REASON_LABELS.manual).color}`}>
                    {(REASON_LABELS[u.last_logout.reason] || REASON_LABELS.manual).label}
                  </span>
                )}
                {expandedUser === u.user_id
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {/* Expanded detail */}
            {expandedUser === u.user_id && (
              <div className="border-t bg-gray-50 px-4 py-3 space-y-3">
                {/* Last Login */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="font-semibold text-gray-500 mb-1.5">Last Login</p>
                    {u.last_login ? (
                      <>
                        <p className="font-medium text-gray-800">{fmt(u.last_login.timestamp)}</p>
                        <div className="flex items-center gap-1 text-gray-500 mt-1">
                          <DeviceIcon device={u.last_login.device} />
                          <span>{u.last_login.browser} · {u.last_login.os}</span>
                        </div>
                        {u.last_login.ip && <p className="text-gray-400 mt-0.5">IP: {u.last_login.ip}</p>}
                      </>
                    ) : <p className="text-gray-400">No record yet</p>}
                  </div>

                  <div className="bg-white rounded-lg p-3 border">
                    <p className="font-semibold text-gray-500 mb-1.5">Last Logout</p>
                    {u.last_logout ? (
                      <>
                        <p className="font-medium text-gray-800">{fmt(u.last_logout.timestamp)}</p>
                        <div className="flex items-center gap-1 text-gray-500 mt-1">
                          <DeviceIcon device={u.last_logout.device} />
                          <span>{u.last_logout.browser} · {u.last_logout.os}</span>
                        </div>
                        <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${(REASON_LABELS[u.last_logout.reason] || REASON_LABELS.manual).color}`}>
                          {(REASON_LABELS[u.last_logout.reason] || REASON_LABELS.manual).label}
                        </span>
                      </>
                    ) : <p className="text-gray-400">No record yet</p>}
                  </div>
                </div>

                {/* Recent activity timeline */}
                {u.recent_activity?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">Recent Activity</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {u.recent_activity.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${a.event === 'login' ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                          <span className={`font-medium w-12 flex-shrink-0 ${a.event === 'login' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {a.event === 'login' ? 'Login' : 'Logout'}
                          </span>
                          <span className="text-gray-500 flex-1 truncate">
                            {fmt(a.timestamp)}
                            {a.reason && a.event === 'logout' && (
                              <span className="ml-1 text-gray-400">· {(REASON_LABELS[a.reason] || REASON_LABELS.manual).label}</span>
                            )}
                          </span>
                          <div className="flex items-center gap-1 text-gray-400 flex-shrink-0">
                            <DeviceIcon device={a.device} />
                            <span>{a.browser}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserActivity;
